import { useEffect, useRef } from 'react';
import { subscribeToTeamSmsMessages } from '../services/firestoreService';
import userTeamService from '../services/userTeamService';
import { previewSmsMessage } from '../utils/smsUtils';
import { notifyInboundSms } from '../utils/commsNotifications';

/**
 * Fire OS (and in-app) alerts when new inbound SMS arrives on the user's team line.
 * Skips the initial Firestore snapshot so existing messages do not notify on load.
 */
export function useInboundSmsOsNotifications(user, { enabled = true } = {}) {
  const seenMessageIdsRef = useRef(new Set());
  const initialSnapshotRef = useRef(true);

  useEffect(() => {
    initialSnapshotRef.current = true;
    seenMessageIdsRef.current = new Set();
  }, [user?.email]);

  useEffect(() => {
    if (!enabled || !user?.email) return undefined;

    const smsConfig = userTeamService.getUserSmsConfig(user.email);
    if (!smsConfig.smsEnabled || !smsConfig.smsNumber) return undefined;

    const unsubscribe = subscribeToTeamSmsMessages(smsConfig.smsNumber, (messages) => {
      if (!Array.isArray(messages)) return;

      if (initialSnapshotRef.current) {
        messages.forEach((msg) => {
          if (msg?.id) seenMessageIdsRef.current.add(msg.id);
        });
        initialSnapshotRef.current = false;
        return;
      }

      messages.forEach((msg) => {
        if (!msg?.id || seenMessageIdsRef.current.has(msg.id)) return;
        seenMessageIdsRef.current.add(msg.id);

        if (msg.direction !== 'inbound') return;
        if (Array.isArray(msg.readBy) && msg.readBy.includes(user.email)) return;

        notifyInboundSms({
          clientName: msg.clientName,
          preview: previewSmsMessage(msg),
          clientId: msg.clientId,
        });
      });
    });

    return unsubscribe;
  }, [user?.email, enabled]);
}
